import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Me, Role } from '@seencast/shared';
import { useMe } from '../api/me.ts';
import { useOrg } from '../api/org.ts';
import { ensureActiveOrg, setActiveOrgId } from '../api/org-store.ts';
import { logout as apiLogout } from '../api/auth.ts';
import { computePermissions, type Permissions } from './permissions.ts';

interface SessionValue {
  me: Me;
  activeOrgId: string;
  role: Role;
  permissions: Permissions;
  switchOrg: (orgId: string) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Wraps authenticated routes. Requires `me` and `org` to be loaded; the caller
 * (AuthGate) guarantees that before mounting this provider.
 */
export function SessionProvider({
  me,
  activeOrgId,
  children,
}: {
  me: Me;
  activeOrgId: string;
  children: ReactNode;
}) {
  const qc = useQueryClient();

  const value = useMemo<SessionValue>(() => {
    const membership = me.orgs.find((o) => o.id === activeOrgId) ?? me.orgs[0];
    const role: Role = membership?.role ?? 'viewer';
    return {
      me,
      activeOrgId,
      role,
      permissions: computePermissions(role, me.user.is_superadmin),
      switchOrg: async (orgId: string) => {
        // Stateless org selection: point the X-Org-Id header at the new org, then
        // refetch everything (all data is org-scoped). No server round-trip.
        setActiveOrgId(orgId);
        await qc.invalidateQueries();
      },
      logout: async () => {
        await apiLogout();
        qc.clear();
        window.location.href = '/login';
      },
    };
  }, [me, activeOrgId, qc]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

export function usePermissions(): Permissions {
  return useSession().permissions;
}

/**
 * Gate for authenticated areas. Fetches `me` + `org`; redirects to /login on
 * 401, shows a spinner while loading, then mounts the session-aware children.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const meQuery = useMe();
  // Seed the active-org store as soon as `me` is known so every org-scoped query
  // carries the right X-Org-Id header (idempotent — writes only when it changes).
  if (meQuery.data) ensureActiveOrg(meQuery.data.orgs);
  const orgQuery = useOrg();

  if (meQuery.isLoading || (meQuery.isSuccess && meQuery.data.orgs.length > 0 && orgQuery.isLoading)) {
    return (
      <div className="loading">
        <span className="spinner" />
      </div>
    );
  }

  if (meQuery.isError || !meQuery.data) {
    // Not authenticated (or session expired) — send to login.
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    return null;
  }

  const activeOrgId = orgQuery.data?.id ?? meQuery.data.orgs[0]?.id ?? '';

  return (
    <SessionProvider me={meQuery.data} activeOrgId={activeOrgId}>
      {children}
    </SessionProvider>
  );
}
